import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Logo } from '../components/Logo';
import PageTitle from '../components/PageTitle';
import OnboardingModal from '../components/OnboardingModal';
import { 
  Play, 
  Clock, 
  Flame, 
  Trophy,
  Calendar,
  Bookmark,
  ChevronRight,
  Zap,
  Target,
  Star,
  TrendingUp,
  LogOut,
  User,
  Settings,
  Gift,
  Video
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [liveClasses, setLiveClasses] = useState([]);
  const [featuredClasses, setFeaturedClasses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    checkOnboarding();
  }, []);

  const checkOnboarding = async () => {
    try {
      const res = await api.get('/users/onboarding');
      if (!res.data.completed) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Failed to check onboarding:', error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      // Save that user skipped onboarding so it doesn't show again
      await api.post('/users/onboarding', {
        machine_type: 'none',
        experience_level: 'beginner',
        fitness_goals: [],
        classes_per_week_goal: 3,
        skipped: true
      });
      setShowOnboarding(false);
    } catch (error) {
      // Even if save fails, hide the modal
      setShowOnboarding(false);
      console.error('Failed to save skip preference:', error);
    }
  };

  const handleOnboardingComplete = async (data) => {
    try {
      await api.post('/users/onboarding', data);
      setShowOnboarding(false);
      toast.success('Welcome to Lagree MVMT! Your journey begins.');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  };

  useEffect(() => {
    let isMounted = true;
    
    const fetchDashboardData = async () => {
      try {
        const [liveRes, featuredRes, progressRes] = await Promise.all([
          api.get('/classes/live'),
          api.get('/classes/featured'),
          api.get('/users/progress')
        ]);

        if (isMounted) {
          setLiveClasses((liveRes.data || []).slice(0, 3));
          setFeaturedClasses((featuredRes.data || []).slice(0, 6));
          setProgress(progressRes.data);

          // Get AI recommendations
          try {
            const recRes = await api.post('/users/recommendations', { limit: 4 });
            if (isMounted) {
              setRecommendations(recRes.data || []);
            }
          } catch {
            if (isMounted) {
              setRecommendations((featuredRes.data || []).slice(0, 4));
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();
    
    return () => {
      isMounted = false;
    };
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const weeklyProgress = progress ? (progress.this_week_completed / progress.weekly_goal) * 100 : 0;

  const categoryIcons = {
    full_body: <Flame className="w-4 h-4" />,
    core: <Target className="w-4 h-4" />,
    lower_body: <TrendingUp className="w-4 h-4" />,
    upper_body: <Zap className="w-4 h-4" />,
    express: <Zap className="w-4 h-4" />,
    fundamentals: <Star className="w-4 h-4" />
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <PageTitle title="Dashboard" />
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/dashboard" size="default" />

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-white font-medium">Home</Link>
            <Link to="/classes" className="text-zinc-400 hover:text-white transition-colors">Classes</Link>
            <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors">Programs</Link>
            <Link to="/shop" className="text-zinc-400 hover:text-white transition-colors">Shop</Link>
            <Link to="/instructors" className="text-zinc-400 hover:text-white transition-colors">Instructors</Link>
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 bg-teal-400 rounded-full flex items-center justify-center text-sm font-bold">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </div>
                <span className="hidden md:inline text-zinc-300">{user?.first_name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-zinc-800">
              {(user?.role === 'instructor' || user?.role === 'admin') && (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/instructor" className="flex items-center gap-2 cursor-pointer text-teal-400 font-medium">
                      <Video className="w-4 h-4" />
                      Instructor Portal
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                </>
              )}
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                  <User className="w-4 h-4" />
                  Profile
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/referrals" className="flex items-center gap-2 cursor-pointer text-teal-400">
                  <Gift className="w-4 h-4" />
                  Refer & Earn $50
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            WELCOME BACK, {user?.first_name?.toUpperCase()}
          </h1>
          <p className="text-zinc-500">Ready to crush your workout?</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Trophy className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Total Classes</span>
            </div>
            <div className="text-2xl font-bold">{progress?.total_classes_completed || 0}</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Total Minutes</span>
            </div>
            <div className="text-2xl font-bold">{progress?.total_minutes || 0}</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Flame className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Current Streak</span>
            </div>
            <div className="text-2xl font-bold text-orange-500">{progress?.current_streak || 0} days</div>
          </div>
          <div className="bg-[#121212] border border-zinc-800 rounded-sm p-4">
            <div className="flex items-center gap-2 text-zinc-500 mb-2">
              <Target className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wider">Weekly Goal</span>
            </div>
            <div className="text-2xl font-bold">{progress?.this_week_completed || 0}/{progress?.weekly_goal || 3}</div>
            <Progress value={Math.min(weeklyProgress, 100)} className="mt-2 h-1.5" />
          </div>
        </div>

        {/* Live Classes Section */}
        {liveClasses.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">UPCOMING LIVE</h2>
              <Link to="/classes?type=live" className="text-teal-400 hover:text-teal-300 flex items-center gap-1 text-sm font-medium">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {liveClasses.map((cls) => (
                <Link 
                  key={cls.id} 
                  to={`/class/${cls.id}`}
                  className="group bg-[#121212] border border-zinc-800 rounded-sm overflow-hidden hover:border-zinc-700 transition-colors"
                  data-testid={`live-class-${cls.id}`}
                >
                  <div className="relative aspect-video">
                    <img 
                      src={cls.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=800&auto=format&fit=crop'} 
                      alt={cls.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 bg-red-500 rounded-sm text-sm font-bold">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      LIVE
                    </div>
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-black fill-black ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                      <Calendar className="w-3 h-3" />
                      {new Date(cls.scheduled_time).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <h3 className="font-bold mb-1">{cls.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500 text-sm">{cls.instructor_name}</span>
                      <span className="text-zinc-600 text-sm">{cls.duration_minutes} min</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Recommended For You */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold">RECOMMENDED FOR YOU</h2>
              <p className="text-zinc-500 text-sm">AI-powered picks based on your history</p>
            </div>
            <Link to="/classes" className="text-teal-400 hover:text-teal-300 flex items-center gap-1 text-sm font-medium">
              Browse All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(recommendations.length > 0 ? recommendations : featuredClasses.slice(0, 4)).map((cls) => (
              <Link 
                key={cls.id} 
                to={`/class/${cls.id}`}
                className="group bg-[#121212] border border-zinc-800 rounded-sm overflow-hidden hover:border-zinc-700 transition-colors card-hover"
                data-testid={`class-card-${cls.id}`}
              >
                <div className="relative aspect-video">
                  <img 
                    src={cls.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=800&auto=format&fit=crop'} 
                    alt={cls.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded-sm text-xs">
                    {cls.duration_minutes} min
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium badge-${cls.category}`}>
                      {cls.category.replace('_', ' ')}
                    </span>
                    <span className={`text-xs difficulty-${cls.difficulty}`}>
                      {cls.difficulty}
                    </span>
                  </div>
                  <h3 className="font-bold text-sm mb-1 line-clamp-1">{cls.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500 text-xs">{cls.instructor_name}</span>
                    {cls.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        <span className="text-xs">{cls.rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Classes */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">FEATURED CLASSES</h2>
            <Link to="/classes" className="text-teal-400 hover:text-teal-300 flex items-center gap-1 text-sm font-medium">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredClasses.slice(0, 6).map((cls) => (
              <Link 
                key={cls.id} 
                to={`/class/${cls.id}`}
                className="group bg-[#121212] border border-zinc-800 rounded-sm overflow-hidden hover:border-zinc-700 transition-colors card-hover"
                data-testid={`featured-class-${cls.id}`}
              >
                <div className="relative aspect-video">
                  <img 
                    src={cls.thumbnail_url || 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=85&w=800&auto=format&fit=crop'} 
                    alt={cls.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                      <Play className="w-6 h-6 text-black fill-black ml-1" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded-sm text-xs">
                    {cls.duration_minutes} min
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium badge-${cls.category}`}>
                      {cls.category.replace('_', ' ')}
                    </span>
                    <span className={`text-xs difficulty-${cls.difficulty}`}>
                      {cls.difficulty}
                    </span>
                  </div>
                  <h3 className="font-bold mb-1">{cls.title}</h3>
                  <p className="text-zinc-500 text-sm line-clamp-2 mb-3">{cls.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">{cls.instructor_name}</span>
                    {cls.rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm">{cls.rating.toFixed(1)}</span>
                        <span className="text-zinc-600 text-xs">({cls.total_ratings})</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="grid md:grid-cols-3 gap-6">
          <Link 
            to="/classes?category=express"
            className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 rounded-sm p-6 hover:border-orange-500/50 transition-colors"
          >
            <Zap className="w-8 h-8 text-orange-500 mb-4" />
            <h3 className="text-xl font-bold mb-2">EXPRESS WORKOUTS</h3>
            <p className="text-zinc-400 text-sm">Quick 20-minute classes for busy schedules</p>
          </Link>

          <Link 
            to="/classes?category=fundamentals"
            className="bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/30 rounded-sm p-6 hover:border-teal-500/50 transition-colors"
          >
            <Target className="w-8 h-8 text-teal-500 mb-4" />
            <h3 className="text-xl font-bold mb-2">FUNDAMENTALS</h3>
            <p className="text-zinc-400 text-sm">Perfect your form and technique</p>
          </Link>

          <Link 
            to="/profile"
            className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-sm p-6 hover:border-purple-500/50 transition-colors"
          >
            <Trophy className="w-8 h-8 text-purple-500 mb-4" />
            <h3 className="text-xl font-bold mb-2">YOUR PROGRESS</h3>
            <p className="text-zinc-400 text-sm">Track achievements and milestones</p>
          </Link>
        </section>
      </main>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal 
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}
    </div>
  );
};

export default Dashboard;
